/**
 * 製品データ取得用カスタムフック（Portal API 経由）
 */
import { useState, useEffect } from 'react';
import { buildApiUrl, freshFetchInit } from '../utils/apiBase.js';

/** CMS 更新反映用のポーリング間隔（5 分） */
const PRODUCTS_POLL_MS = 5 * 60 * 1000;

const parseMonthlyPrice = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/[^\d.-]/g, '');
    if (normalized === '') {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'off', ''].includes(normalized)) {
      return false;
    }
  }
  return defaultValue;
};

const normalizeProduct = (rawProduct) => {
  const monthlyPrice = parseMonthlyPrice(rawProduct.monthlyPrice);
  const freeNote = typeof rawProduct.freeNote === 'string' ? rawProduct.freeNote : '';
  const priceNote = typeof rawProduct.priceNote === 'string' ? rawProduct.priceNote : '';
  const isFree = parseBoolean(rawProduct.isFree, false) || monthlyPrice === 0;

  return {
    ...rawProduct,
    monthlyPrice,
    freeNote,
    priceNote,
    isFree,
    isComingSoon: parseBoolean(rawProduct.isComingSoon, false),
    isActive: parseBoolean(rawProduct.isActive, true),
    features: Array.isArray(rawProduct.features) ? rawProduct.features : [],
    useCases: Array.isArray(rawProduct.useCases) ? rawProduct.useCases : [],
    launchUrl: rawProduct.launchUrl || '',
    launchUrlDev: rawProduct.launchUrlDev || '',
    shortDescription: rawProduct.shortDescription || '',
    description: rawProduct.description || '',
    trialPeriodDays: rawProduct.trialPeriodDays ?? null,
    betaTrialPeriodDays: rawProduct.betaTrialPeriodDays ?? null,
  };
};

export const useProducts = (options = {}) => {
  const { freshOnMount = false } = options;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchProducts = async (isInitial) => {
      if (isInitial) {
        setLoading(true);
      }
      try {
        const fetchInit = freshOnMount ? freshFetchInit() : undefined;
        const res = await fetch(buildApiUrl('/api/products'), fetchInit);
        if (!res.ok) {
          throw new Error('製品の取得に失敗しました');
        }
        const data = await res.json();
        if (cancelled) return;

        const productsData = (data.products || [])
          .map((product) => normalizeProduct(product))
          .filter((product) => product.isActive);

        setProducts(productsData);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          console.error('製品取得エラー:', err);
          setError(err.message || '製品の取得に失敗しました');
        }
      } finally {
        if (!cancelled && isInitial) {
          setLoading(false);
        }
      }
    };

    fetchProducts(true);
    const intervalId = setInterval(() => fetchProducts(false), PRODUCTS_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [freshOnMount]);

  return { products, loading, error };
};

/**
 * アイコンタイプに対応するSVGアイコンを取得
 */
export const getProductIcon = (iconType) => {
  const icons = {
    growth: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22V8"/>
        <path d="M5 12H2a10 10 0 0 0 20 0h-3"/>
        <path d="M12 8a4 4 0 0 0-4-4H5"/>
        <path d="M12 8a4 4 0 0 1 4-4h3"/>
      </svg>
    ),
    mft: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="9"/>
        <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
        <line x1="9" y1="9" x2="9.01" y2="9"/>
        <line x1="15" y1="9" x2="15.01" y2="9"/>
      </svg>
    ),
    insurance: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="4" width="20" height="16" rx="2"/>
        <path d="M12 8v8"/>
        <path d="M8 12h8"/>
        <path d="M6 8h2"/>
        <path d="M6 16h2"/>
        <path d="M16 8h2"/>
        <path d="M16 16h2"/>
      </svg>
    ),
    mouthpiece: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        <path d="M8 7h8"/>
        <path d="M8 11h8"/>
        <path d="M8 15h4"/>
      </svg>
    ),
    inventory: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
    ),
    analytics: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    chart: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
    portal: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    default: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7"/>
        <rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/>
        <rect x="3" y="14" width="7" height="7"/>
      </svg>
    )
  };

  return icons[iconType] || icons.default;
};

export const getProductPriceInfo = (product) => {
  if (!product) {
    return {
      isFree: false,
      hasPrice: false,
      monthlyPrice: null,
      freeNote: '',
      priceNote: ''
    };
  }

  const monthlyPrice = parseMonthlyPrice(product.monthlyPrice);
  const freeNote = typeof product.freeNote === 'string' ? product.freeNote : '';
  const priceNote = typeof product.priceNote === 'string' ? product.priceNote : '';
  const isFree = parseBoolean(product.isFree, false) || monthlyPrice === 0;
  // isFree 時は monthlyPrice を温存していても実行時は無料扱い（有料設定の復元用）
  const hasPrice = !isFree && Number.isFinite(monthlyPrice) && monthlyPrice > 0;

  return {
    isFree,
    hasPrice,
    monthlyPrice,
    freeNote,
    priceNote
  };
};

export const formatProductPrice = (product, { includeTaxNote = false } = {}) => {
  const priceInfo = getProductPriceInfo(product);

  if (priceInfo.isFree) {
    return priceInfo.freeNote ? `無料（${priceInfo.freeNote}）` : '無料';
  }

  if (!priceInfo.hasPrice) {
    return '価格未設定';
  }

  const yen = `¥${priceInfo.monthlyPrice.toLocaleString()}/月`;
  if (!includeTaxNote) {
    return priceInfo.priceNote ? `${yen}（${priceInfo.priceNote}）` : yen;
  }
  const withTaxNote = `${yen}（税別）`;
  return priceInfo.priceNote ? `${withTaxNote}（${priceInfo.priceNote}）` : withTaxNote;
};

/**
 * 有料価格の表示（税別は小さめ muted）。無料・未設定はそのまま。
 */
export const ProductPriceLabel = ({ product, prefix = '' }) => {
  const priceInfo = getProductPriceInfo(product);

  if (priceInfo.isFree) {
    return (
      <span>
        {prefix}{priceInfo.freeNote ? `無料（${priceInfo.freeNote}）` : '無料'}
      </span>
    );
  }

  if (!priceInfo.hasPrice) {
    return <span>{prefix}価格未設定</span>;
  }

  return (
    <span>
      {prefix}¥{priceInfo.monthlyPrice.toLocaleString()}/月
      <span className="hub-price-tax-note">税別</span>
      {priceInfo.priceNote ? `（${priceInfo.priceNote}）` : null}
    </span>
  );
};

export default useProducts;
